import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import InstanceDetailStore from "./InstanceDetailStore";
import InstanceActionsStore from "../InstanceActions/InstanceActionsStore";
import type ClientInstancesStore from "../ClientInstances/ClientInstancesStore";
import type InstanceEnvStore from "../InstanceEnv/InstanceEnvStore";

/**
 * InstanceDetail console route — /infrastructure/instances/:id.
 *
 * Hidden, parameterized route (the Client Instances list owns the nav entry).
 * Reads require auth (viewer+); the live log/stats streams are staff-only and
 * the exec terminal is admin-only — all enforced by the API. The route lifecycle
 * drives the store's stream subscribe/teardown:
 *   - onEnter / onParamsChange → load the record + subscribe to logs/stats.
 *   - onExit → dispose() tears down EVERY stream + the exec session.
 * The view also disposes on unmount, so streams never leak.
 *
 * Dialogs: Deploy / Rollback / Confirm action / Move to client are registered
 * here (operator+).
 * The confirm dialog's onSubmit runs the lifecycle action through the detail
 * store so the loaded record + streams stay coherent.
 */
const instanceDetailRoute: RouteType[] = [
  {
    name: "InstanceDetail",
    path: "/infrastructure/instances/:id",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Instance Console",
        enableRefreshButton: true,
        component: () => import("./InstanceDetail.Header"),
      },
      body: {
        component: () => import("./InstanceDetail.Body"),
        grayBackground: true,
      },
    },
    group: "Infrastructure",
    hidden: true,
    requiresAuth: true,
    requiresPermission: false,
    // Real-time streaming view — requires connectivity.
    offlineCapable: false,
    onEnter: ({ rootStore, params }) => {
      const store = rootStore.instanceDetailStore as InstanceDetailStore;
      const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
      const id = params.id as string;
      if (id) {
        store.initialize(id);
        actions?.loadAudit(id);
        // The Environment card (admins only — the store reads nothing otherwise).
        void (rootStore.instanceEnvViewStore as InstanceEnvStore | undefined)?.load(id);
      }
    },
    onParamsChange: ({ rootStore, params }) => {
      const store = rootStore.instanceDetailStore as InstanceDetailStore;
      const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
      const id = params.id as string;
      if (id && store.instanceId !== id) {
        store.initialize(id);
        actions?.loadAudit(id);
        void (rootStore.instanceEnvViewStore as InstanceEnvStore | undefined)?.load(id);
      }
    },
    onExit: ({ rootStore }) => {
      const store = rootStore.instanceDetailStore as InstanceDetailStore;
      // Tear down every live stream + the exec session on navigate-away.
      store?.dispose();
      // …and drop any revealed environment value or typed secret.
      (rootStore.instanceEnvViewStore as InstanceEnvStore | undefined)?.leave();
    },
    dialogs: [
      {
        // The Environment card's review: the change the API checked, keys
        // only for secrets, then the apply with the shared progress panel.
        name: "InstanceEnvChangeDialog",
        title: "Apply environment change",
        component: () => import("../InstanceEnv/EnvChangeDialog.Body"),
        cancelButton: true,
        height: 560,
        submitButtonText: "Apply & restart",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const env = rootStore.instanceEnvViewStore as InstanceEnvStore;
          // A refused or failed apply throws: the dialog stays open with the
          // reason in its footer (shared/dialogSubmit). The card keeps the draft.
          throwIfFailed(await env.apply(), env.applyError, "The environment change failed.");
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const env = rootStore.instanceEnvViewStore as InstanceEnvStore;
          return !!env.preview && env.isAdmin && !env.applying;
        },
      },
      {
        name: "InstanceDeployDialog",
        title: "Deploy Image",
        component: () => import("../InstanceActions/dialogs/DeployDialog.Body"),
        cancelButton: true,
        height: 420,
        submitButtonText: "Deploy",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const detail = rootStore.instanceDetailStore as InstanceDetailStore;
          // A refused deploy throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitDeploy(), actions.deployError, "The image could not be deployed.");
          await detail.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isDeployValid && actions.canManage;
        },
      },
      {
        name: "InstanceRollbackDialog",
        title: "Roll Back Image",
        component: () =>
          import("../InstanceActions/dialogs/RollbackDialog.Body"),
        cancelButton: true,
        height: 520,
        submitButtonText: "Roll Back",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const detail = rootStore.instanceDetailStore as InstanceDetailStore;
          // A refused rollback throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitRollback(), actions.rollbackError, "The rollback could not be started.");
          await detail.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isRollbackValid && actions.canManage;
        },
      },
      {
        name: "InstanceConfirmActionDialog",
        title: "Confirm Action",
        component: () =>
          import("../InstanceActions/dialogs/ConfirmActionDialog.Body"),
        cancelButton: true,
        // Tall enough for the progress panel the body shows once Confirm is tapped.
        height: 440,
        submitButtonText: "Confirm",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const detail = rootStore.instanceDetailStore as InstanceDetailStore;
          // Run the confirmed action through the detail store's runner so the
          // loaded record + streams stay coherent. 'remove' soft-deletes the
          // row itself — reloading the detail after would just 404 — so it
          // navigates back to the grid on success instead.
          await actions.runConfirmed(async (instance, action, progressToken) => {
            if (action === "remove") {
              const grid = rootStore.clientInstancesViewStore as ClientInstancesStore;
              const ok = await grid.removeInstance(instance);
              if (ok) rootStore.routerStore?.goTo?.("ClientInstances");
              return ok;
            }
            if (action === "update-to-latest") {
              // Server-resolved (the API picks the digest), so it has its own
              // runner. It reaches this dialog because the audit found Update
              // firing on one tap with no confirmation — on a phone it is the
              // most prominent control in a cluster of ~36px buttons, and it
              // redeploys a production container.
              const ok = await actions.updateToLatest(instance);
              if (ok) await detail.reload();
              return ok;
            }
            return detail.runLifecycleFromDetail(instance, action, progressToken);
          });
        },
      },
      {
        name: "InstanceReassignDialog",
        title: "Move to Client",
        component: () => import("../InstanceActions/dialogs/ReassignDialog.Body"),
        cancelButton: true,
        height: 520,
        submitButtonText: "Recreate & move",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const detail = rootStore.instanceDetailStore as InstanceDetailStore;
          const ok = await actions.submitReassign();
          if (ok) await detail.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isReassignValid && actions.canManage;
        },
      },
    ],
  },
];

export default instanceDetailRoute;
